import SLS_pedidosVendas from '@/api/Operacoes/sls_pedidosVendas';
import Notifier from '@/libs/Notifier';
import { backorderCountFilters, totalBackordersResponse } from '@/utils/pedidoListingRows';

export default {
  namespaced: true,
  state: () => ({
    setIsLoading: false,
    setIsLoadinglistarCapaPedido: false,
    setIsLoadinglistarEntregaPedido: false,
    setIsLoadinglistarItensPedido: false,
    setIsLoadingSalvarItens: false,
    setIsLoadingCancelarPedido: false,
    setPedidoVendas: [],
    // Rascunhos em estado PRÓPRIO: pedidos e rascunhos compartilhavam a mesma
    // lista/meta e a troca de aba fazia os pedidos "sumirem" (a segunda
    // resposta sobrescrevia a primeira).
    setRascunhos: [],
    metaRascunhos: {},
    setCapaPedido: {},
    setItensPedido: [],
    itensRequestVersion: 0,
    setEnderecoEntrega: {},
    filters: {},
    filtersRascunhos: {},
    meta: {},
    filtersItens: {},
    metaItens: {},
  }),
  actions: {
    // Consulta compartilhada sem alterar lista, filtros, meta ou loading.
    async buscarPedidosVendas({ state }, filters = state.filters) {
      try {
        const params = { ...filters };

        try {
          const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado'));

          if (perfilLogado) {
            if (perfilLogado.perfil_acesso_nome === 'Representante') {
              // O escopo REAL é resolvido no servidor pelo token (x-perfil-id
              // → todos os representantes vinculados ao usuário). O
              // representanteData local só existe quando o usuário tem
              // EXATAMENTE 1 rep — com 2+ vínculos ele fica ausente e NÃO
              // pode bloquear a listagem (bug: tela vazia "0 pedidos").
              params.perfilTipo = 'Representante';
              const representanteData = JSON.parse(localStorage.getItem('representanteData') || 'null');
              if (representanteData && representanteData.representanteid) {
                params.perfilId = representanteData.representanteid;
              }
            } else if (['Cliente', 'ClienteFinal'].includes(perfilLogado.perfil_acesso_nome)) {
              // O perfil de cliente final pode se chamar 'Cliente' OU 'ClienteFinal'
              // (varia por tenant) — ambos devem ver só os próprios pedidos.
              // clienteData é salvo aninhado ({ cliente: { clienteid } });
              // tolera também o formato achatado por compatibilidade.
              const clienteData = JSON.parse(localStorage.getItem('clienteData') || 'null');
              const clienteid = clienteData?.cliente?.clienteid ?? clienteData?.clienteid;
              if (clienteid) {
                params.perfilTipo = perfilLogado.perfil_acesso_nome;
                params.perfilId = clienteid;
              } else {
                // Fail-closed: sem clienteid NÃO chamamos a rota sem escopo
                // (mostraria pedidos de outros clientes).
                throw new Error('Não foi possível identificar seu cadastro de cliente. Saia e entre novamente.');
              }
            }
          }
        } catch (error) {
          console.error('Erro ao obter informações de perfil:', error);
          throw error;
        }

        return await new SLS_pedidosVendas().getPedidosVendas(params);
      } catch (error) {
        // this.notify pode não existir no contexto da store — não pode
        // mascarar o erro original com "reading 'error' of undefined".
        if (this.notify && typeof this.notify.error === 'function') this.notify.error(error.message);
        throw error;
      }
    },
    async contarBackorders({ state, dispatch }) {
      const response = await dispatch('buscarPedidosVendas', backorderCountFilters(state.filters));
      return totalBackordersResponse(response);
    },
    async buscarPedidosVendasExportacao({ state, dispatch }) {
      // Snapshot no início: a resposta nunca restaura/sobrescreve a tela atual.
      const filters = {
        ...state.filters,
        page: 1,
        pageSize: Math.max(Number(state.meta.total) || 0, 1),
      };
      const response = await dispatch('buscarPedidosVendas', filters);
      if (!response || !Array.isArray(response.data)) {
        throw new Error('Resposta inválida ao exportar os pedidos.');
      }
      return response.data;
    },
    async retornaPedidoVendas({ commit, dispatch }) {
      try {
        commit('SET_IS_LOADING', true);
        const { data, meta } = await dispatch('buscarPedidosVendas');
        commit('SET_PEDIDO_VENDAS', data);
        commit('SET_META', meta);
      } finally {
        commit('SET_IS_LOADING', false);
      }
    },
    async retornaRascunhosPedidoVendas({ commit, state }) {
      try {
        commit('SET_IS_LOADING', true);

        const params = { ...state.filtersRascunhos };

        try {
          const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado'));

          if (perfilLogado) {
            if (perfilLogado.perfil_acesso_nome === 'Representante') {
              // Escopo real vem do servidor via token (todos os reps do
              // usuário); representanteData é opcional (só existe com 1 rep).
              params.perfilTipo = 'Representante';
              const representanteData = JSON.parse(localStorage.getItem('representanteData') || 'null');
              if (representanteData && representanteData.representanteid) {
                params.perfilId = representanteData.representanteid;
              }
            } else if (['Cliente', 'ClienteFinal'].includes(perfilLogado.perfil_acesso_nome)) {
              // Mesmo tratamento do retornaPedidoVendas: 'Cliente' ou 'ClienteFinal';
              // clienteData aninhado ({ cliente: { clienteid } }) ou achatado.
              const clienteData = JSON.parse(localStorage.getItem('clienteData') || 'null');
              const clienteid = clienteData?.cliente?.clienteid ?? clienteData?.clienteid;
              if (clienteid) {
                params.perfilTipo = perfilLogado.perfil_acesso_nome;
                params.perfilId = clienteid;
              } else {
                // Fail-closed: sem clienteid NÃO chamamos a rota sem escopo.
                throw new Error('Não foi possível identificar seu cadastro de cliente. Saia e entre novamente.');
              }
            }
          }
        } catch (error) {
          console.error('Erro ao obter informações de perfil:', error);
          throw error;
        }

        const { data, meta } = await new SLS_pedidosVendas().getRascunhosPedidosVendas(params);
        commit('SET_RASCUNHOS', data);
        commit('SET_META_RASCUNHOS', meta);
      } catch (error) {
        // this.notify pode não existir no contexto da store — não pode
        // mascarar o erro original com "reading 'error' of undefined".
        if (this.notify && typeof this.notify.error === 'function') this.notify.error(error.message);
        throw error;
      } finally {
        commit('SET_IS_LOADING', false);
      }
    },
    async cancelarPedido({ commit, state }, pedidoid) {
      try {
        commit('SET_IS_LOADING_CANCELAR_PEDIDO', true);
        await new SLS_pedidosVendas().cancelarPedido(pedidoid);
      } catch (error) {
        // this.notify pode não existir no contexto da store — não pode
        // mascarar o erro original com "reading 'error' of undefined".
        if (this.notify && typeof this.notify.error === 'function') this.notify.error(error.message);
        throw error;
      } finally {
        commit('SET_IS_LOADING_CANCELAR_PEDIDO', false);
      }
    },
    async listarCapaPedido({ commit, state }, pedidoid) {
      try {
        commit('SET_IS_LOADING_CAPA_PEDIDO_VENDA', true);
        const { data } = await new SLS_pedidosVendas().listarCapaPedido(pedidoid);
        commit('SET_CAPA_PEDIDO', data);
      } catch (error) {
        // this.notify pode não existir no contexto da store — não pode
        // mascarar o erro original com "reading 'error' of undefined".
        if (this.notify && typeof this.notify.error === 'function') this.notify.error(error.message);
        throw error;
      } finally {
        commit('SET_IS_LOADING_CAPA_PEDIDO_VENDA', false);
      }
    },
    async salvarItensNovos({ commit, state }, data) {
      try {
        commit('SET_IS_LOADING_SALVAR_ITENS', true);
        await new SLS_pedidosVendas().salvarItens(data);
      } catch (error) {
        // this.notify pode não existir no contexto da store — não pode
        // mascarar o erro original com "reading 'error' of undefined".
        if (this.notify && typeof this.notify.error === 'function') this.notify.error(error.message);
        throw error;
      } finally {
        commit('SET_IS_LOADING_SALVAR_ITENS', false);
      }
    },
    async enderecoEntregaPedido({ commit, state }, clienteid) {
      try {
        commit('SET_IS_LOADING_ENTREGA_PEDIDO', true);
        const response = await new SLS_pedidosVendas().enderecoEntregaPedido(clienteid);
        commit('SET_ENDERECO_ENTREGA', (response && response.data) || {});
      } catch (error) {
        commit('SET_ENDERECO_ENTREGA', {});
        // this.notify pode não existir no contexto da store — não pode
        // mascarar o erro original com "reading 'error' of undefined".
        if (this.notify && typeof this.notify.error === 'function') this.notify.error(error.message);
        throw error;
      } finally {
        commit('SET_IS_LOADING_ENTREGA_PEDIDO', false);
      }
    },
    async retornaItensPedido({ commit, state }, pedidoid) {
      const version = (Number(state.itensRequestVersion) || 0) + 1;
      state.itensRequestVersion = version;
      commit('SET_ITENS_PEDIDO', []);
      try {
        commit('SET_IS_LOADING_ITENS_PEDIDO', true);
        const { dataItens, meta } = await new SLS_pedidosVendas().itensPedido(pedidoid, state.filtersItens);
        if (version !== state.itensRequestVersion) return;

        // Mapear os itens para incluir os campos de impostos
        const itensComImpostos = dataItens.map((item) => ({
          ...item,
          PrecoComImposto: parseFloat(item.PrecoComImposto || 0),
          BaseIcms: parseFloat(item.BaseIcms || 0),
          ValorIcms: parseFloat(item.ValorIcms || 0),
          valorTotalComImposto: item.itens.reduce((acc, subItem) => acc + ((subItem.precoComImposto || 0) * subItem.pedidoItemQuantidade), 0),
          itens: item.itens.map((subItem) => ({
            ...subItem,
            precoComImposto: parseFloat(subItem.precoComImposto || 0),
            baseIcms: parseFloat(subItem.baseIcms || 0),
            valorIcms: parseFloat(subItem.valorIcms || 0),
            valorTotalComImposto: parseFloat(subItem.precoComImposto || 0) * subItem.pedidoItemQuantidade,
          })),
        }));

        commit('SET_ITENS_PEDIDO', itensComImpostos);
        commit('SET_META_ITENS', meta);
      } catch (error) {
        // this.notify pode não existir no contexto da store — não pode
        // mascarar o erro original com "reading 'error' of undefined".
        if (this.notify && typeof this.notify.error === 'function') this.notify.error(error.message);
        throw error;
      } finally {
        if (version === state.itensRequestVersion) commit('SET_IS_LOADING_ITENS_PEDIDO', false);
      }
    },
    async setFilters({ commit }, filters) {
      commit('SET_FILTERS', filters);
    },
    async setFiltersRascunhos({ commit }, filters) {
      commit('SET_FILTERS_RASCUNHOS', filters);
    },
    async replaceFilters({ commit }, filters) {
      commit('REPLACE_FILTERS', filters);
    },
    async replaceFiltersRascunhos({ commit }, filters) {
      commit('REPLACE_FILTERS_RASCUNHOS', filters);
    },
    async setFiltersItens({ commit }, filters) {
      // console.log('Action Filters:', filters);
      commit('SET_FILTERS_ITENS', filters);
    },
    async clearFilters({ commit }) {
      commit('clearFilters');
    },
    async setItensPedido({ commit, state }, data) {
      commit('SET_ITENS_PEDIDO', data);
    },
    async setItensTabelaPedido({ commit, state }, data) {
      commit('SET_ITENS_TABELA_PEDIDO', data);
    },
  },
  mutations: {
    SET_IS_LOADING(state, data) {
      state.setIsLoading = data;
    },
    SET_IS_LOADING_CANCELAR_PEDIDO(state, data) {
      state.setIsLoadingCancelarPedido = data;
    },
    SET_IS_LOADING_CAPA_PEDIDO_VENDA(state, data) {
      state.setIsLoadinglistarCapaPedido = data;
    },
    SET_IS_LOADING_SALVAR_ITENS(state, data) {
      state.setIsLoadingSalvarItens = data;
    },
    SET_IS_LOADING_ENTREGA_PEDIDO(state, data) {
      state.setIsLoadinglistarEntregaPedido = data;
    },
    SET_IS_LOADING_ITENS_PEDIDO(state, data) {
      state.setIsLoadinglistarItensPedido = data;
    },
    SET_PEDIDO_VENDAS(state, data) {
      state.setPedidoVendas = data;
    },
    // SET_FILTERS(state, data) {
    //     console.log('SET_FILTERS', data)
    //     state.filters = data;
    //     console.log("Updated Filters:", state.filters);
    // },
    SET_FILTERS(state, data) {
      const next = { ...state.filters };
      Object.entries(data || {}).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          delete next[key];
          return;
        }
        if (typeof value === 'number' && isNaN(value)) {
          delete next[key];
          return;
        }
        next[key] = value;
      });
      state.filters = next;
    },
    SET_FILTERS_RASCUNHOS(state, data) {
      const next = { ...state.filtersRascunhos };
      Object.entries(data || {}).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          delete next[key];
          return;
        }
        if (typeof value === 'number' && isNaN(value)) {
          delete next[key];
          return;
        }
        next[key] = value;
      });
      state.filtersRascunhos = next;
    },
    REPLACE_FILTERS(state, data) {
      state.filters = { ...(data || {}) };
    },
    REPLACE_FILTERS_RASCUNHOS(state, data) {
      state.filtersRascunhos = { ...(data || {}) };
    },
    clearFilters(state) {
      state.filters = {};
    },
    SET_META(state, data) {
      state.meta = data;
    },
    SET_RASCUNHOS(state, data) {
      state.setRascunhos = data;
    },
    SET_META_RASCUNHOS(state, data) {
      state.metaRascunhos = data;
    },
    SET_CAPA_PEDIDO(state, data) {
      state.setCapaPedido = data;
    },
    SET_ITENS_PEDIDO(state, data) {
      state.setItensPedido = data;
    },
    SET_ITENS_TABELA_PEDIDO(state, data) {
      const exist = state.setItensPedido.find((item) => item.referencia === data.referencia);

      if (exist) {
        exist.Qtde += data.Qtde;
        exist.ValorTotal += data.ValorTotal;

        data.itens.forEach((novoItem) => {
          const existVariacao = exist.itens.find((item) => item.cdncor === novoItem.cdncor && item.desc_tamanho === novoItem.desc_tamanho);
          if (existVariacao) {
            existVariacao.pedidoItemQuantidade += novoItem.pedidoItemQuantidade;
            existVariacao.valorTotal += novoItem.valorTotal;
          } else {
            exist.itens.push(novoItem);
          }
        });
      } else {
        state.setItensPedido.push(data);
      }
    },
    SET_FILTERS_ITENS(state, data) {
      const sanitized = { ...state.filtersItens };
      Object.entries(data || {}).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (typeof v === 'number' && isNaN(v)) return;
        if (v === '') return;
        sanitized[k] = v;
      });
      state.filtersItens = sanitized;
    },
    SET_META_ITENS(state, data) {
      state.metaItens = data;
    },
    SET_ENDERECO_ENTREGA(state, data) {
      state.setEnderecoEntrega = data || {};
    },
  },
  getters: {
    getIsLoading: (state) => state.setIsLoading,
    getPedidoVendas: (state) => state.setPedidoVendas,
    getRascunhos: (state) => state.setRascunhos,
    getMetaRascunhos: (state) => state.metaRascunhos,
    getFilters: (state) => state.filters,
    getFiltersRascunhos: (state) => state.filtersRascunhos,
    getMeta: (state) => state.meta,
    getCapaPedido: (state) => state.setCapaPedido,
    getItensPedido: (state) => state.setItensPedido,
    getEnderecoEntrega: (state) => state.setEnderecoEntrega,
    getFiltersItens: (state) => state.filtersItens,
    getMetaItens: (state) => state.metaItens,
  },
};
